<template>
    <div class="page-header-index-wide">
        <div class="onboarding">
            <div class="head">
                <h2>Acceso a la plataforma</h2>
                <h3 class="text-linube">Cuentas, usuarios, roles y permisos</h3>
            </div>
            <a-row>
                <a-col :span="24" class="block-justify">
                    Estás leyendo éste documento, así que has podido acceder con tus credenciales, ¡bien!
                    <br />
                    A continuación te detallamos una serie de conceptos relacionados con los permisos, para
                    ayudarte a entender mejor la organización y los accesos configurables para ti y otros
                    miembros de tu organización.
                </a-col>
            </a-row>
            <a-row>
                <a-col :span="16" class="block-justify">
                    <p>
                        <h3 class="text-linube">
                            Cuentas
                        </h3>

                        Una cuenta generalmente representa un cliente del proveedor de servicios o un departamento
                        dentro de una gran organización. Una cuenta puede contener múltiples usuarios.

                        <h3 class="text-linube">
                            Roles
                        </h3>

                        Un rol representa un conjunto de funciones permitidas. Todas las cuentas de CloudStack tienen un
                        rol asignado que aplica reglas de acceso para permitir o denegar solicitudes de API.
                        Normalmente, hay cuatro roles predeterminados: administrador raíz, administrador de recursos,
                        administrador de dominio y usuario. Además, se han agregado nuevos roles, como Administrador de
                        Solo Lectura, Usuario de Solo Lectura, Administrador de Soporte y Usuario de Soporte, que a su
                        vez están basados en los roles mencionados anteriormente.
                       
                        <h3 class="text-linube">
                            Dominios
                        </h3>

                        Las cuentas se agrupan en dominios. Los dominios suelen contener múltiples cuentas que tienen
                        una relación lógica entre sí y administradores delegados con cierta autoridad sobre el dominio y
                        sus subdominios. Por ejemplo, un proveedor de servicios con varios revendedores podría crear un
                        dominio para cada revendedor.
                        <br/>

                        Además del tipo de cuenta Administrador Raíz (disponible solo en el dominio raíz), se pueden
                        crear dos tipos diferentes de cuentas dentro de cada dominio: Administrador de Dominio y
                        Usuario.
                        <h3 class="text-linube">
                            Usuarios
                        </h3>

                        Los usuarios funcionan como alias dentro de una cuenta. Los usuarios de la misma cuenta no están
                        aislados entre sí, pero sí están aislados de los usuarios de otras cuentas. En la mayoría de las
                        instalaciones, no es necesario exponer el concepto de usuarios, ya que normalmente hay un solo
                        usuario por cuenta. Un mismo usuario no puede pertenecer a múltiples cuentas.
                        <br/>
                        El nombre de usuario es único dentro de un dominio en todas las cuentas de ese dominio. Sin
                        embargo, el mismo nombre de usuario puede existir en otros dominios, incluidos los subdominios.
                        Un nombre de dominio puede repetirse solo si la ruta completa desde la raíz es única. Por
                        ejemplo, es posible crear root/d1, root/foo/d1 y root/sales/d1. 
                        <br/>
                        Los administradores son cuentas con privilegios especiales en el sistema. Puede haber múltiples
                        administradores en el sistema, y estos pueden crear o eliminar otros administradores, así como
                        cambiar la contraseña de cualquier usuario.
                        <br/>

                        <h4 class="text-linube">
                            Tipos de Administradores
                        </h4>
                        Administradores de Dominio

                        Los administradores de dominio pueden realizar operaciones administrativas para los usuarios
                        dentro de su dominio. Sin embargo, no tienen visibilidad sobre los servidores físicos ni sobre
                        otros dominios.
                        <br/>

                        Administrador Raíz

                        Los administradores raíz tienen acceso completo al sistema, lo que les permite administrar
                        plantillas, ofertas de servicio, administradores de atención al cliente y dominios.
                        Administrador de Solo Lectura

                        Un rol de administrador restringido en el que la cuenta solo puede ejecutar operaciones de
                        listar, obtener o buscar, pero no puede realizar ninguna acción que modifique la
                        infraestructura, configuración o recursos de los usuarios.
                        Usuario de Solo Lectura

                        Un rol de usuario restringido en el que la cuenta solo puede ejecutar operaciones de listar,
                        obtener o buscar. Este rol está destinado a usuarios que solo necesitan monitorear el sistema y
                        el uso de recursos.
                        <br/>
                        <h4 class="text-linube">
                            Administrador de Soporte
                            
                        </h4>

                        Un rol de administrador restringido en el que la cuenta solo puede realizar tareas de soporte y
                        mantenimiento auxiliares que no afectan directamente la infraestructura. Por ejemplo, puede
                        crear ofertas y poner recursos en mantenimiento, pero no puede modificar redes físicas.
                        <br/>
                        <h4 class="text-linube">
                            Usuario de Soporte

                        </h4>


                        Un rol de usuario restringido en el que la cuenta no puede crear ni eliminar recursos, pero sí
                        puede visualizar recursos y realizar tareas de soporte, como iniciar o detener instancias,
                        adjuntar o desmontar volúmenes e ISOs, entre otras.
                        <br/>
                        <h4 class="text-linube">
                            Propiedad de los Recursos
                            
                        </h4>

                        Los recursos pertenecen a la cuenta, no a los usuarios individuales dentro de esa cuenta. Por
                        ejemplo, la facturación y los límites de recursos se mantienen a nivel de cuenta, no de usuario.
                        <br/>

                        Un usuario puede operar sobre cualquier recurso de la cuenta siempre que tenga los permisos
                        adecuados. Estos permisos están determinados por el rol asignado.
                        <br/>

                        Un administrador raíz puede cambiar la propiedad de cualquier instancia de una cuenta a otra
                        utilizando la API assignVirtualMachine. De manera similar, un administrador de dominio o
                        subdominio puede reasignar instancias dentro del dominio a cualquier otra cuenta en el mismo
                        dominio o en cualquiera de sus subdominios. </p>
                </a-col>
                <a-col :span="8" class="block-center">
                    <img class="block-center-img" src="/assets/onboarding/0-project-new.png" />
                </a-col>
            </a-row>
            <a-row>
                <a-col :span="24" class="block-justify">
                    <h3 class="text-linube">Acceso al proyecto</h3>
                </a-col>
                <a-col :span="24" class="block-center">
                    <img class="block-center-img" src="/assets/onboarding/2-project-view.png" />

                </a-col>
                <a-col :span="24" class="block-justify">
                    <p>
                        Al acceder al proyecto, verás un panel de control distinto, donde aparecerán los
                        recursos asignados únicamente a éste proyecto dentro de tu cuenta. La barra lateral
                        sirve como indicador de que las instancias, redes o servicios que despliegues que hagas
                        se harán siempre dentro de los límites de éste proyecto.
                    </p>
                </a-col>
            </a-row>
        </div>
    </div>
</template>

<script>

export default {
    name: 'FirstProject',
    components: {
    },
    data() {
        return {

        }
    },
    mounted() {

    },
    methods: {

    }
}
</script>
<style scoped lang="scss">
.onboarding {
    font-family: sans-serif;
    padding: 20px 10px 50px 10px;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);

    @media (min-width: 760px) {
        padding: 20px 10% 50px 10%;
    }
}

.text-linube {
    color: #4DB1D9;
}

.block-center {
    padding-left: 20px;
    padding-right: 20px;
    text-align: center;
}

.block-center-img {
    width: 100%;
    height: auto;
}

.block-justify {
    padding-left: 20px;
    padding-right: 20px;
    text-align: justify;
}
</style>
